import { Injectable } from "@angular/core";
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";

import { AuthenticationService } from "../services/auth.service";
import { AuthfakeauthenticationService } from "../services/authfake.service";

import { environment } from "../../../environments/environment";
import { AuthService } from "../services/auth/auth.service";
import { getRequiredPermissions, routeRequiresPermissions } from "../models/route-permissions.model";
import { PERMISSIONS } from "../models/permissions.models";

@Injectable({ providedIn: "root" })
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private authService: AuthService,
    private authFackservice: AuthfakeauthenticationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this.authService.getUserInfo();

    // Check if user is authenticated
    if (!currentUser) {
      this.router.navigate(["/account/login"], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    // Handle dashboard redirect based on permissions
    if (state.url === "/dashboard" || state.url === "/") {
      return this.handleDashboardRedirect(currentUser);
    }

    // Check if route requires specific permissions
    const requiredPermissions = getRequiredPermissions(state.url);
    
    // If route requires permissions, check if user has them
    if (requiredPermissions.length > 0) {
      const hasAccess = this.authService.hasAnyPermission(requiredPermissions);
      
      if (!hasAccess) {
        // Redirect to dashboard if user doesn't have required permissions
        console.warn(`Access denied to ${state.url}. Required permissions:`, requiredPermissions, 'User permissions:', currentUser.permissions);
        this.router.navigate(["/dashboard"]);
        return false;
      }
    }

    // Allow access if user is authenticated and has required permissions (or no permissions required)
    return true;
  }

  private handleDashboardRedirect(user: any): boolean {
    // SuperUser or users with BoxView permission → su_dashboard
    if (this.authService.isSuperUser() || this.authService.hasPermission(PERMISSIONS.BoxView)) {
      this.router.navigate(["/su_dashboard"]);
      return true;
    }

    // Users with LightView permission → su_light_boxes
    if (this.authService.hasPermission(PERMISSIONS.LightView)) {
      this.router.navigate(["/su_light_boxes"]);
      return true;
    }

    // Users with SparePartsView permission → spare_parts_boxes
    if (this.authService.hasPermission(PERMISSIONS.SparePartsView)) {
      this.router.navigate(["/spare_parts_boxes"]);
      return true;
    }

    // Users with PalletsView or PalletsManage permission → su_pallets
    if (this.authService.hasAnyPermission([PERMISSIONS.PalletsView, PERMISSIONS.PalletsManage])) {
      this.router.navigate(["/su_pallets"]);
      return true;
    }

    // Users with any permission → redirect to first available route
    const userPermissions = user.permissions || [];
    if (userPermissions.length > 0) {
      // Try to find a suitable default route based on first permission
      if (userPermissions.includes(PERMISSIONS.BoxView)) {
        this.router.navigate(["/su_dashboard"]);
        return true;
      }
      if (userPermissions.includes(PERMISSIONS.LightView)) {
        this.router.navigate(["/su_light_boxes"]);
        return true;
      }
      if (userPermissions.includes(PERMISSIONS.SparePartsView)) {
        this.router.navigate(["/spare_parts_boxes"]);
        return true;
      }
      if (userPermissions.includes(PERMISSIONS.PalletsView) || userPermissions.includes(PERMISSIONS.PalletsManage)) {
        this.router.navigate(["/su_pallets"]);
        return true;
      }
    }

    // No permissions or pending user → redirect to pending landing
    this.router.navigate(["/pe_landing"]);
    return true;
  }
}
